import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import {
  PanelCommandItem,
  PanelCommandKey,
  PanelFeeder
} from "../../interfaces/panel-alerts.interface";

/**
 * Columna de comandos del panel de testeos.
 *
 * Solo pinta: recibe los comandos con su estado ya calculado y devuelve al
 * padre cual hay que lanzar. El vaciado conserva su propio componente porque
 * tiene opciones y confirmacion propias.
 */
@Component({
  selector: "app-panel-commands",
  templateUrl: "./commands.html",
  styleUrls: ["./commands.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanelCommandsComponent {
  /** Comandos visibles con su estado. */
  @Input() items: PanelCommandItem[] = [];
  /** Alimentadores del lote, que necesita el componente de vaciado. */
  @Input() feeders: PanelFeeder[] = [];

  /** Se emite con el comando que el usuario quiere lanzar. */
  @Output() run = new EventEmitter<PanelCommandKey>();
  /** Se emite con la respuesta del vaciado. */
  @Output() emptySubmitted = new EventEmitter<any>();

  /** Comandos regulares (sin interruptor, sin vaciado que tiene su propio card). */
  get regularItems(): PanelCommandItem[] {
    return this.items.filter((item) => !item.is_toggle && item.key !== "empty");
  }

  /**
   * Comandos de tipo interruptor (Encender / Apagar), con Encender siempre
   * a la izquierda y Apagar a la derecha.
   */
  get toggleItems(): PanelCommandItem[] {
    const toggles = this.items.filter((item) => !!item.is_toggle);
    const on = toggles.filter((i) => i.key === "turn_on");
    const off = toggles.filter((i) => i.key === "turn_off");
    return [...on, ...off];
  }

  /** Alimentadores que ya respondieron al comando de vaciado. */
  get emptyRespondedFeeders(): PanelFeeder[] {
    return this.feeders.filter(
      (f) => f.loadings && (f.loadings.empty === "done" || f.loadings.empty === "error")
    );
  }

  /**
   * Identifica cada comando para que Angular reutilice los botones al
   * recalcularse la lista.
   * @param index Posicion en la lista.
   * @param item Comando de esa posicion.
   * @returns Clave estable del comando.
   */
  trackByKey(index: number, item: PanelCommandItem): string {
    return item.key;
  }

  /**
   * Reenvia al padre el comando elegido.
   * @param key Comando a lanzar.
   */
  onRun(key: PanelCommandKey): void {
    this.run.emit(key);
  }

  /**
   * Reenvia al padre la respuesta del vaciado.
   * @param event Respuesta del backend, equipos afectados y si se detenia el vaciado.
   */
  onEmpty(event: any): void {
    this.emptySubmitted.emit(event);
  }

  /**
   * Devuelve el item de comando del vaciado para mostrar el contador de
   * respuestas en el control de vaciado.
   * @returns Item del vaciado, o null si aun no hay datos.
   */
  get emptyCommandItem(): PanelCommandItem | null {
    return this.items.find((item) => item.key === "empty") || null;
  }
}
